import type { DocMeta } from '../db';
import { formatDate, formatDateTime, formatNumber } from '../format';
import type { CollectedTask } from '../tasks';
import { isBuiltinField, parseQueryBlock, type QueryField, type QuerySpec } from './parse';
import { runQuery, runTaskQuery, type QueryDeps, type QueryHit } from './run';

/**
 * Filling `div.mb-query` blocks with the documents they ask for.
 *
 * Results are `.mb-wikilink` anchors, which is not a detail: the preview
 * already opens those, creating the note if it does not exist, so a query list
 * behaves exactly like a link written by hand — one navigation path, not two.
 *
 * Everything is built with DOM calls rather than HTML strings, so a document
 * title can never become markup on its way into a list of titles.
 */

export interface QueryLabels {
  empty: string;
  emptyTasks: string;
  countTasks: (n: number) => string;
  /** Accessible name for a checkbox, which is otherwise just "checkbox". */
  toggleTask: (text: string) => string;
  count: (n: number) => string;
  problems: (what: string) => string;
  field: (field: QueryField) => string;
  /** Accessible name for the column button that re-orders a table. */
  sortBy: (field: QueryField) => string;
  /** Accessible name for an editable property cell. */
  editField: (field: QueryField, title: string) => string;
  untitled: string;
}

/** A property value as text: what you would have written above the note. */
function propertyText(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map(String).join(', ');
  return String(value);
}

function link(doc: DocMeta, labels: QueryLabels): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'mb-wikilink';
  a.href = '#';
  a.dataset.title = doc.title;
  a.textContent = doc.title || labels.untitled;
  return a;
}

/**
 * One cell.
 *
 * A built-in column is a fact about the note and is shown as text. A
 * frontmatter property is an input, because the point of seeing your own
 * properties in a table is fixing one without opening five notes — and the
 * write goes into the note's frontmatter, so the file stays the truth.
 */
function cell(hit: QueryHit, field: QueryField, deps: QueryDeps, labels: QueryLabels): HTMLElement {
  const doc: DocMeta = hit.doc;
  const wrap = document.createElement('span');

  if (!isBuiltinField(field)) {
    const value = propertyText(hit.props?.[field]);
    if (!deps.setField) {
      wrap.textContent = value;
      return wrap;
    }
    const input = document.createElement('input');
    input.className = 'mb-query-prop';
    input.value = value;
    input.setAttribute('aria-label', labels.editField(field, doc.title || labels.untitled));
    input.addEventListener('change', () => void deps.setField?.(doc.id, field, input.value));
    wrap.appendChild(input);
    return wrap;
  }

  switch (field) {
    case 'title':
      wrap.appendChild(link(doc, labels));
      return wrap;
    case 'updated':
      wrap.textContent = formatDate(doc.updatedAt);
      wrap.title = formatDateTime(doc.updatedAt);
      return wrap;
    case 'created':
      wrap.textContent = formatDate(doc.createdAt);
      wrap.title = formatDateTime(doc.createdAt);
      return wrap;
    case 'folder':
      wrap.textContent = doc.folder ?? '';
      return wrap;
    case 'tags':
      wrap.textContent = doc.tags.join(', ');
      return wrap;
    default:
      wrap.textContent = formatNumber(doc.size);
      return wrap;
  }
}

function renderList(hits: QueryHit[], labels: QueryLabels): HTMLElement {
  const ul = document.createElement('ul');
  ul.className = 'mb-query-list';
  for (const hit of hits) {
    const li = document.createElement('li');
    li.appendChild(link(hit.doc, labels));
    if (hit.doc.excerpt) {
      const excerpt = document.createElement('span');
      excerpt.className = 'mb-query-excerpt';
      excerpt.textContent = hit.doc.excerpt.slice(0, 120);
      li.appendChild(excerpt);
    }
    ul.appendChild(li);
  }
  return ul;
}

/** Order by one column, for the table's clickable headers. */
function byField(field: QueryField, ascending: boolean) {
  const value = (hit: QueryHit): string | number => {
    switch (field) {
      case 'updated':
        return hit.doc.updatedAt;
      case 'created':
        return hit.doc.createdAt;
      case 'size':
        return hit.doc.size;
      case 'title':
        return (hit.doc.title || '').toLowerCase();
      case 'folder':
        return (hit.doc.folder ?? '').toLowerCase();
      case 'tags':
        return hit.doc.tags.join(', ').toLowerCase();
      default:
        return propertyText(hit.props?.[field]).toLowerCase();
    }
  };
  return (a: QueryHit, b: QueryHit): number => {
    const [x, y] = [value(a), value(b)];
    const order =
      typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return ascending ? order : -order;
  };
}

/**
 * A table whose columns can be clicked to re-order it.
 *
 * The order is not written back into the note: a query block says what it
 * wants, and looking at the same rows from another angle should not rewrite the
 * document you are reading.
 */
function renderTable(
  hits: QueryHit[],
  spec: QuerySpec,
  deps: QueryDeps,
  labels: QueryLabels,
): HTMLElement {
  const table = document.createElement('table');
  table.className = 'mb-query-table';
  let sorted = [...hits];
  let sortField: QueryField | null = null;
  let ascending = true;

  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  const body = document.createElement('tbody');

  const fill = () => {
    body.replaceChildren();
    for (const hit of sorted) {
      const row = document.createElement('tr');
      for (const field of spec.fields) {
        const td = document.createElement('td');
        td.appendChild(cell(hit, field, deps, labels));
        row.appendChild(td);
      }
      body.appendChild(row);
    }
    for (const th of headRow.children) {
      const field = (th as HTMLElement).dataset.field;
      th.setAttribute(
        'aria-sort',
        field && field === sortField ? (ascending ? 'ascending' : 'descending') : 'none',
      );
    }
  };

  for (const field of spec.fields) {
    const th = document.createElement('th');
    th.scope = 'col';
    th.dataset.field = field;
    const button = document.createElement('button');
    button.className = 'mb-query-sort';
    button.textContent = labels.field(field);
    button.setAttribute('aria-label', labels.sortBy(field));
    button.addEventListener('click', () => {
      ascending = sortField === field ? !ascending : true;
      sortField = field;
      sorted = [...hits].sort(byField(field, ascending));
      fill();
    });
    th.appendChild(button);
    headRow.appendChild(th);
  }
  head.appendChild(headRow);
  table.append(head, body);
  fill();
  return table;
}

/** The same rows read as a board rather than a spreadsheet. */
function renderCards(
  hits: QueryHit[],
  spec: QuerySpec,
  deps: QueryDeps,
  labels: QueryLabels,
): HTMLElement {
  const grid = document.createElement('div');
  grid.className = 'mb-query-cards';
  for (const hit of hits) {
    const card = document.createElement('article');
    card.className = 'mb-query-card';

    const heading = document.createElement('h4');
    heading.appendChild(link(hit.doc, labels));
    card.appendChild(heading);

    if (hit.doc.excerpt) {
      const excerpt = document.createElement('p');
      excerpt.className = 'mb-query-excerpt';
      excerpt.textContent = hit.doc.excerpt.slice(0, 160);
      card.appendChild(excerpt);
    }

    for (const field of spec.fields) {
      if (field === 'title') continue;
      const row = document.createElement('p');
      row.className = 'mb-query-card-field';
      const name = document.createElement('span');
      name.className = 'mb-query-card-label';
      name.textContent = labels.field(field);
      row.append(name, cell(hit, field, deps, labels));
      card.appendChild(row);
    }
    grid.appendChild(card);
  }
  return grid;
}

/**
 * Tasks gathered from other notes, each one tickable.
 *
 * The checkbox writes back to the note the task lives in, which is the point:
 * an inbox you cannot tick off is a report, not an inbox. Every write carries
 * the text it saw, so a note that changed in the meantime is left alone (see
 * lib/tasks.ts).
 */
function renderTasks(tasks: CollectedTask[], deps: QueryDeps, labels: QueryLabels): HTMLElement {
  const ul = document.createElement('ul');
  ul.className = 'mb-query-tasks';
  for (const task of tasks) {
    const li = document.createElement('li');
    li.dataset.docId = task.docId;
    li.dataset.line = String(task.line);

    const box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = task.checked;
    box.setAttribute('aria-label', labels.toggleTask(task.text));
    box.disabled = !deps.setTaskChecked;
    box.addEventListener('change', () => {
      void deps.setTaskChecked?.(task.docId, task.line, box.checked, task.text);
    });
    li.appendChild(box);

    const text = document.createElement('span');
    text.className = 'mb-query-task-text';
    text.textContent = task.text;
    li.appendChild(text);

    const source = document.createElement('a');
    source.className = 'mb-wikilink mb-query-task-source';
    source.href = '#';
    source.dataset.title = task.docTitle;
    source.textContent = task.docTitle;
    li.appendChild(source);

    ul.appendChild(li);
  }
  return ul;
}

/** Fill every query block under `root`. Safe to call again on the same root. */
export async function renderQueries(
  root: ParentNode,
  deps: QueryDeps,
  labels: QueryLabels,
  currentDocId?: string,
): Promise<void> {
  const blocks = [...root.querySelectorAll<HTMLElement>('.mb-query')];
  for (const block of blocks) {
    // The source is the block's text on first render, and kept afterwards so a
    // re-render (the library changed) does not read its own output.
    const source = block.dataset.querySource || block.textContent || '';
    block.dataset.querySource = source;

    const spec = parseQueryBlock(source);
    const tasks = spec.tasks ? await runTaskQuery(spec, deps, currentDocId) : null;
    const hits = tasks ? [] : await runQuery(spec, deps, currentDocId);

    block.replaceChildren();
    if (spec.problems.length) {
      const problems = document.createElement('p');
      problems.className = 'mb-query-problem';
      problems.textContent = labels.problems(spec.problems.join(' · '));
      block.appendChild(problems);
    }

    const found = tasks ? tasks.length : hits.length;
    if (found === 0) {
      const empty = document.createElement('p');
      empty.className = 'mb-query-empty';
      empty.textContent = tasks ? labels.emptyTasks : labels.empty;
      block.appendChild(empty);
      continue;
    }

    block.appendChild(
      tasks
        ? renderTasks(tasks, deps, labels)
        : spec.layout === 'table'
          ? renderTable(hits, spec, deps, labels)
          : spec.layout === 'cards'
            ? renderCards(hits, spec, deps, labels)
            : renderList(hits, labels),
    );
    const count = document.createElement('p');
    count.className = 'mb-query-count';
    count.textContent = tasks ? labels.countTasks(found) : labels.count(found);
    block.appendChild(count);
  }
}
